import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import _last from 'lodash/last';
import { getProjectFinancials } from '../../../features/project/projectSlice';
import { makeDefaultQofEBridgeOptions } from '../highchart-utilities';
import { createQofESeries } from './createQofESeries';
import { screenReaderPointDescriptorFactory } from './createScreenReaderPointDescription';
import { barGlobalColors } from './highchartsColorTheme';
import { getTimePeriodsForQofE } from '../../engine/selectors/timePeriodSelectors';
import { getReportedEbitda } from '../../engine/selectors/factCalculationsSelectors';
import { getAdjustedEBITDA } from '../../engine/selectors/aggregatorSelectors';
import { getAdjustmentSumsParticipatingInCalculationsForQofE } from '../../engine/selectors/adjustmentSelectors';
import { getFinancialScale } from '../../services/textService';
import './QofEBridgeChart.scss';

const QofEBridgeChart = () => {
  highchartsAccessibility(Highcharts);
  highchartsMore(Highcharts);
  Highcharts.setOptions({
    colors: barGlobalColors,
  });

  const [chartOptions, setChartOptions] = useState(
    makeDefaultQofEBridgeOptions(0)
  );

  const financialsDisplaySettings = useSelector(
    getProjectFinancials,
    shallowEqual
  );
  const qofeFinancialPeriods = useSelector(getTimePeriodsForQofE);
  const reportedEbitda = useSelector(getReportedEbitda);
  const adjustedEbitda = useSelector(getAdjustedEBITDA);
  const qofeAdjustmentSumsParticipatingCalculations = useSelector(
    getAdjustmentSumsParticipatingInCalculationsForQofE
  );

  useEffect(() => {
    const seriesData = createQofESeries(
      qofeFinancialPeriods,
      reportedEbitda,
      adjustedEbitda,
      qofeAdjustmentSumsParticipatingCalculations,
      Highcharts.getOptions().colors[1],
      financialsDisplaySettings.financialsDecimalCount,
      Highcharts.getOptions().colors[7]
    );

    const lastFinancialPeriod = _last(qofeFinancialPeriods);
    const subtitle = `${lastFinancialPeriod} - ${getFinancialScale(
      financialsDisplaySettings
    )}`;

    const pointDescriptorFn = screenReaderPointDescriptorFactory(
      financialsDisplaySettings.financialsDecimalCount
    );

    setChartOptions({
      series: [
        {
          data: seriesData,
        },
      ],
      xAxis: {
        max: seriesData.length - 1 + 0.25,
      },
      subtitle: {
        text: subtitle,
      },
      accessibility: {
        point: {
          descriptionFormatter: pointDescriptorFn,
        },
      },
    });
  }, [
    financialsDisplaySettings,
    qofeFinancialPeriods,
    reportedEbitda,
    adjustedEbitda,
    qofeAdjustmentSumsParticipatingCalculations,
  ]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div className="bridge-chart-container" tabIndex="0">
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
};

export default QofEBridgeChart;
